import React, { useState } from 'react';
import { useFormContext, ValidationIndicator, FieldHintAndError } from '@/components/form';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

const FormInputInline = ({
  name = "_",
  label,
  type = 'text',
  validators,
  required = false,
  placeholder,
  hint,
  className
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [originalValue, setOriginalValue] = useState('');
  const form = useFormContext();

  const requiredValidator = ({ value }) => {
    if (required && (!value || value.toString().trim() === '')) {
      return 'This field is required';
    }

    if (validators) {
      return validators({ value });
    }

    return undefined;
  };

  const finalValidators = validators ? validators : { onBlur: requiredValidator };

  const handleEditClick = (currentValue) => {
    setOriginalValue(currentValue);
    setIsEditing(true);
  };

  const handleCancel = (field) => {
    field.handleChange(originalValue);
    setIsEditing(false);
  };

  const handleSave = (field) => {
    const hasError = field.state.meta.errors.length > 0;
    if (!hasError) {
      setIsEditing(false);
    }
  };

  return (
    <form.Field
      name={name}
      validators={finalValidators}
    >
      {(field) => {
        const hasError = field.state.meta.isTouched && field.state.meta.errors.length > 0;

        if (!isEditing) {
          return (
            <div className={cn("space-y-2", className)}>
              <div className="flex items-center gap-2 group cursor-pointer" onClick={() => handleEditClick(field.state.value)}>
                <span className="text-lg font-medium text-gray-700 border-b border-dashed border-gray-300 pb-1">
                  {field.state.value || placeholder}
                </span>
                <svg 
                  className="w-4 h-4 text-gray-400 opacity-40 group-hover:opacity-80 transition-opacity" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
            </div>
          );
        }

        return (
          <div className={cn("relative", className)}>
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black bg-opacity-25 z-40" onClick={() => handleCancel(field)} />
            
            {/* Modal */}
            <div className="absolute top-0 left-0 z-50 bg-white rounded-lg shadow-lg border p-4 w-80">
              <div className="space-y-3">
                <Label
                  htmlFor={field.name}
                  className={cn("text-sm font-medium", hasError ? 'text-destructive' : 'text-gray-900')}
                >
                  {label}
                  {(required || validators) && (
                    <span className={hasError ? 'text-destructive' : 'text-muted-foreground'}>
                      {' *'}
                    </span>
                  )}
                </Label>
                <div className="relative">
                  <Input
                    id={field.name}
                    type={type}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSave(field);
                      } else if (e.key === 'Escape') {
                        handleCancel(field);
                      }
                    }}
                    placeholder={placeholder}
                    className={cn(
                      "w-full",
                      hasError ? 'border-destructive focus:ring-destructive' : 'border-blue-500 focus:ring-blue-500'
                    )}
                    autoFocus
                  />
                  <ValidationIndicator field={field} />
                </div>
                <FieldHintAndError field={field} hint={hint} />
                
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => handleCancel(field)}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSave(field)}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 border border-transparent rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      }}
    </form.Field>
  );
};

export default FormInputInline;
export { FormInputInline };